import { Injectable } from '@angular/core';
import { createStore } from '@ngneat/elf';
import {
  addEntities,
  deleteAllEntities,
  deleteEntities,
  deleteEntitiesByPredicate,
  selectAllEntities,
  selectAllEntitiesApply,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { Observable } from 'rxjs';
import { ApiLocationsAdapterService } from '@fullyops/data/locations/api-adapter.service';
import {
  Location,
  LocationInput,
  MiniLocation,
} from '@fullyops/data/locations/location';
import { tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LocationService {
  private store = createStore(
    { name: 'locations' },
    withEntities<Location>({ idKey: 'id' }),
  );

  private miniStore = createStore(
    { name: 'mini-locations' },
    withEntities<MiniLocation>({ idKey: 'id' }),
  );

  constructor(private api: ApiLocationsAdapterService) {}

  // This exists because using locationsByCompanyId$ is inconvenient for the asset form (and also
  // unnecessarily inefficient)
  readonly locations$ = this.store.pipe(selectAllEntities());

  readonly miniLocations$ = this.miniStore.pipe(selectAllEntities());

  locationsByCompanyId$(companyId: string) {
    return this.store.pipe(
      selectAllEntitiesApply({
        filterEntity: (location: Location) => location.companyId == companyId,
      }),
    );
  }

  refreshMini(): void {
    this.api
      .getMini()
      .subscribe((res) => this.miniStore.update(setEntities(res)));
  }

  refreshByCompanyId(companyId: string) {
    this.api
      .getAllByCompany(companyId)
      .subscribe((res) => this.store.update(setEntities(res)));
  }

  getById(id: string): Observable<Location> {
    return this.api.getById(id);
  }

  create(loc: LocationInput): Observable<Location> {
    return this.api
      .create(loc)
      .pipe(tap((newLocation) => this.store.update(addEntities(newLocation))));
  }

  update(locationId: string, loc: LocationInput): Observable<Location> {
    return this.api
      .update(locationId, loc)
      .pipe(
        tap((updatedLocation) =>
          this.store.update(updateEntities(locationId, updatedLocation)),
        ),
      );
  }

  delete(id: string): Observable<void> {
    return this.api
      .delete(id)
      .pipe(tap(() => this.store.update(deleteEntities(id))));
  }

  clearByCompanyId(companyId: string) {
    this.store.update(
      deleteEntitiesByPredicate((location) => location.companyId === companyId),
    );
  }

  clear() {
    this.store.update(deleteAllEntities());
  }

  clearMini(): void {
    this.miniStore.update(deleteAllEntities());
  }
}
